import {
  CALENDAR_PLATFORMS,
  type CalendarPlanSlot,
  type CalendarPlatform,
  type PublicationBadgeStatus,
  type SlotDeliverableSummary,
} from "@/types/contentCalendar.type";

/**
 * Platforms with an automatic publisher today.
 *
 * Keep this list aligned with the scheduling capability registry
 * (backend `domain/Scheduling/platformCapabilities.ts`). Exporting it gives
 * calendar warnings and compact channel marks one source of truth.
 *
 * Snapchat is deliberately absent. Its integration is finished on the backend
 * but parked in `SNAPCHAT_CAPABILITY_PARKED`: Snap never allowlisted the Public
 * Profile API for our client id, so a publish could only ever 403. Until that
 * approval lands Snapchat behaves like LinkedIn — planned, briefed, posted by
 * hand. Re-add it here at the same time as the backend entry, never before.
 */
export const AUTO_PUBLISHABLE_PLATFORMS = [
  "TIKTOK",
  "INSTAGRAM",
  "YOUTUBE",
  "FACEBOOK",
] as const satisfies readonly CalendarPlatform[];

const AUTO_PUBLISHABLE = new Set<CalendarPlatform>(AUTO_PUBLISHABLE_PLATFORMS);

export function isAutoPublishablePlatform(platform: CalendarPlatform): boolean {
  return AUTO_PUBLISHABLE.has(platform);
}

export type PublicationIntegrityState =
  | "ready"
  | "setup_required"
  | "publishing"
  | "scheduled"
  | "partially_scheduled"
  | "retry_scheduled"
  | "partially_live"
  | "live"
  | "failed"
  | "manual_required";

export const PUBLICATION_INTEGRITY_LABEL: Record<PublicationIntegrityState, string> = {
  ready: "Ready",
  setup_required: "Publishing setup required",
  publishing: "Publishing",
  scheduled: "Scheduled",
  partially_scheduled: "Partially scheduled",
  retry_scheduled: "Retry scheduled",
  partially_live: "Partially live",
  live: "Live",
  failed: "Failed",
  manual_required: "Manual posting required",
};

export type PublicationChannelState =
  | "published"
  | "failed"
  | "retrying"
  | "manual"
  | "setup"
  | "cancelled"
  | "pending"
  | "publishing"
  | "queued";

type PublicationSummary = SlotDeliverableSummary["publications"][number];

export interface PublicationChannelRead {
  platform: CalendarPlatform;
  state: PublicationChannelState;
  /** Null means the intended channel has no auto-publication row yet. */
  publicationStatus: PublicationBadgeStatus | null;
  scheduledAt: string | null;
  permalink: string | null;
}

export interface PublicationIntegrityRead {
  /** Null until an asset is ready for publishing. */
  state: PublicationIntegrityState | null;
  /** True only when every current intended channel is published. */
  complete: boolean;
  /** Publishing needs a person: failure, manual work, setup, an unscheduled row, or no target. */
  needsAction: boolean;
  intendedChannels: CalendarPlatform[];
  channels: PublicationChannelRead[];
  publishedChannels: CalendarPlatform[];
  failedChannels: CalendarPlatform[];
  manualChannels: CalendarPlatform[];
  /** Auto-publishable targets that do not have a publication row yet. */
  setupChannels: CalendarPlatform[];
  /** Current targets whose previous publication row was cancelled. */
  cancelledChannels: CalendarPlatform[];
  pendingChannels: CalendarPlatform[];
  publishingChannels: CalendarPlatform[];
  queuedChannels: CalendarPlatform[];
  retryingChannels: CalendarPlatform[];
}

type IntegritySlot = Pick<
  CalendarPlanSlot,
  "origin" | "channels" | "ideas" | "deliverable"
>;

/** Current targets, not the immutable history of places a post once appeared. */
export function intendedPublicationChannels(slot: IntegritySlot): CalendarPlatform[] {
  const raw =
    slot.origin === "DIRECT"
      ? slot.channels
      : (slot.ideas?.find((idea) => idea.status === "ACCEPTED")?.channels ?? []);

  const known = new Set<CalendarPlatform>(CALENDAR_PLATFORMS);
  const seen = new Set<CalendarPlatform>();
  return raw.filter((platform): platform is CalendarPlatform => {
    const candidate = platform as CalendarPlatform;
    if (!known.has(candidate) || seen.has(candidate)) return false;
    seen.add(candidate);
    return true;
  });
}

function channelState(
  platform: CalendarPlatform,
  publication: PublicationSummary | undefined,
): PublicationChannelState {
  if (!publication) return isAutoPublishablePlatform(platform) ? "setup" : "manual";
  if (publication?.status === "PUBLISHED") return "published";
  if (publication?.status === "FAILED") {
    return publication.nextAttemptAt ? "retrying" : "failed";
  }
  if (publication?.status === "PUBLISHING") return "publishing";
  if (publication?.status === "SCHEDULED") return "queued";
  if (publication.status === "CANCELLED") return "cancelled";
  if (!isAutoPublishablePlatform(platform)) return "manual";
  // DRAFT and READY have a real publication row and can be scheduled. Missing
  // and cancelled rows are kept separate: both need setup before a date can be
  // chosen, so neither may be described as schedule-ready.
  return "pending";
}

/**
 * Roll up per-channel publication state without letting one success hide the
 * rest. Only current intended channels are the denominator; historical live
 * rows left behind after a channel edit remain audit history, not completion.
 */
export function publicationIntegrityOf(slot: IntegritySlot): PublicationIntegrityRead {
  const intendedChannels = intendedPublicationChannels(slot);
  const deliverable = slot.deliverable ?? null;
  const publications = deliverable?.publications ?? [];
  const byPlatform = new Map(publications.map((publication) => [publication.platform, publication]));

  const channels = intendedChannels.map((platform): PublicationChannelRead => {
    const publication = byPlatform.get(platform);
    return {
      platform,
      state: channelState(platform, publication),
      publicationStatus: publication?.status ?? null,
      scheduledAt: publication?.scheduledAt ?? null,
      permalink: publication?.permalink ?? null,
    };
  });

  const inState = (state: PublicationChannelState) =>
    channels.filter((channel) => channel.state === state).map((channel) => channel.platform);
  const publishedChannels = inState("published");
  const failedChannels = inState("failed");
  const manualChannels = inState("manual");
  const setupChannels = inState("setup");
  const cancelledChannels = inState("cancelled");
  const pendingChannels = inState("pending");
  const publishingChannels = inState("publishing");
  const queuedChannels = inState("queued");
  const retryingChannels = inState("retrying");
  const assetReady = deliverable?.status === "READY";
  const complete =
    assetReady &&
    intendedChannels.length > 0 &&
    publishedChannels.length === intendedChannels.length;

  let state: PublicationIntegrityState | null = null;
  if (assetReady) {
    state = complete
      ? "live"
      : publishedChannels.length > 0
        ? "partially_live"
        : failedChannels.length > 0
          ? "failed"
          : setupChannels.length > 0 || cancelledChannels.length > 0
            ? "setup_required"
            : manualChannels.length > 0
              ? "manual_required"
              : pendingChannels.length > 0 &&
                  (publishingChannels.length > 0 ||
                    retryingChannels.length > 0 ||
                    queuedChannels.length > 0)
                ? "partially_scheduled"
                : publishingChannels.length > 0
                  ? "publishing"
                  : retryingChannels.length > 0
                    ? "retry_scheduled"
                    : queuedChannels.length > 0
                      ? "scheduled"
                      : "ready";
  }

  return {
    state,
    complete,
    needsAction:
      assetReady &&
      (intendedChannels.length === 0 ||
        failedChannels.length > 0 ||
        manualChannels.length > 0 ||
        setupChannels.length > 0 ||
        cancelledChannels.length > 0 ||
        pendingChannels.length > 0),
    intendedChannels,
    channels,
    publishedChannels,
    failedChannels,
    manualChannels,
    setupChannels,
    cancelledChannels,
    pendingChannels,
    publishingChannels,
    queuedChannels,
    retryingChannels,
  };
}
